import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography } from '@mui/material'
import { LocationDisplayLayout } from 'Components/Cards/LocationCard/LocationDisplay'
import { type location } from 'core/types'
import { type ChildInfoDetails, ChildInfoLayout } from 'Components/ChildDetails/ChildInfoLayout'
import { useState } from 'react'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import LaunchIcon from '@mui/icons-material/Launch'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import { useAppSelector } from 'hooks/reduxHooks'
import { type ChildDuplicate, dupeSelector, originalSelector } from 'slices/duplicateChildSlice'
import { useGetDuplicateThresholdsQuery } from 'slices/globalSettingSlice'
import { useNavigate, generatePath } from 'react-router'
import { CARENETWORK_MANAGEMENT_DETAILS } from 'Pages/Routes'

function TranslateChildDetails (val: ChildDuplicate): ChildInfoDetails {
  return {
    ...val,
    uic: '',
    imageFile: '',
    childRaceList: (val.race != null && val.race.length > 0) ? val.race.split(',') : '',
    eligibilityFactorList: [],
    environmentalRiskList: [],
    middleName: ''
  } as unknown as ChildInfoDetails
}
function TranslateLocation (val: ChildDuplicate): location {
  return {
    ...val
  } as unknown as location
}

export function ChildDuplicateDisplay (): JSX.Element {
  const existingRecord = useAppSelector(originalSelector)
  const comparingRecord = useAppSelector(dupeSelector)
  const { data } = useGetDuplicateThresholdsQuery()
  const nav = useNavigate()
  const loadExistingRecord = (): void => {
    if (existingRecord?.[0]?.childId != null) {
      const path = generatePath(CARENETWORK_MANAGEMENT_DETAILS, { id: existingRecord[0].childId.toString() ?? '' })
      nav(path)
    }
  }
  const matchValue = existingRecord?.[0]?.matchedValues?.reduce((accumulator, matchValue) => accumulator + Number((matchValue.points ?? 0)), 0) ?? 0
  let warningText = ' Based on this probability you are required to provide an additional explanation. '
  const [expand, setExpand] = useState(false)
  let backgroundColor = '#DDEBF1'
  if (existingRecord == null || comparingRecord == null || data == null) { return <>Duplicate information was not loaded correctly.</> }

  let result = ''
  let descriptionOfResult = ''
  switch (true) {
    case (matchValue === 100) :
      result = 'Extremely Likely (100 Points)'
      descriptionOfResult = 'extremely likely'
      warningText = ' Based on this probability someone will need to approve this duplicate.  Please provide an explanation. '
      backgroundColor = '#DEBB32'
      break
    case (matchValue >= data.acceptanceThreshold) :
      result = `Very Likely (>= ${data.acceptanceThreshold} Points)`
      descriptionOfResult = 'very likely'
      warningText = ' Based on this probability someone will need to approve this duplicate.  Please provide an explanation. '
      backgroundColor = '#DEBB32'

      break
    case (matchValue >= data.warningThreshold):
      result = `Moderately Likely (>= ${data.warningThreshold} Points)`
      descriptionOfResult = 'moderately likely'
      break
  }

  return <Box sx={{ display: 'box', maxWidth: '700px', padding: '20px', backgroundColor: '#FAFAFA', border: '1px solid black', borderRadius: '15px' } }>
    <Typography sx={{ justifySelf: 'center', fontWeight: 'bold', fontSize: '25px' }}>Duplicate Details</Typography>
    <Box sx={{ display: 'flex', flexDirection: 'row' } }>
      <Box sx={{ display: 'flex', flexDirection: 'column', px: '10px', width: '50%' }} data-testid='existingRecordContainer'>
        <Typography fontWeight={'bold'}>Existing Record</Typography>
        <DupePanel {...existingRecord[0]} />
        {existingRecord[0]?.childId != null && existingRecord[0]?.childId !== 0 && existingRecord[0]?.canAccess && (
          <Button variant='text'
            onClick={loadExistingRecord}
            sx={{ width: 'fit-content', alignSelf: 'center', textTransform: 'none', textDecoration: 'underline', color: 'black' }}
          >See Full Child Record<LaunchIcon /></Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', px: '10px', width: '50%' }} data-testid='newRecordContainer'>
        <Typography fontWeight={'bold'}>New Record</Typography>
        <DupePanel {...comparingRecord} />
      </Box>

    </Box>
    <Accordion
      expanded={expand}
      onChange={() => {}}
      data-testid={'accordionButton'}
      sx={{
        width: '100%',
        border: '1px solid black',
        borderRadius: '12px !important',
        overflow: 'hidden',
        margin: '0px !important',
        cursor: 'default !important',
        background: backgroundColor + '30',
        borderColor: backgroundColor
      }}
    >
      <AccordionSummary
        expandIcon={<></>}
        aria-controls="panel1bh-content"
        id="panel1bh-header"
        className={''}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '95px',
          cursor: 'default !important'

        }}
      >
        <Box sx={{ height: '70px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <ErrorOutlineIcon sx={{ height: '40px', width: '40px' }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: '18px', height: '70px', justifyContent: 'center', alignItems: 'left' }}>
          <Typography sx={{}} variant='body1'>
            <>This match is {descriptionOfResult} to be a duplicate.
              {warningText}
              Want to know more about how we determined this? Click</>
            <Button onClick={() => { setExpand(!expand) }} data-testid='expandDetail'
              variant='text'
              sx={{
                minWidth: '0px',
                textTransform: 'none',
                color: 'black',
                textDecoration: 'underline',
                px: '5px',
                py: '0px !important'
              }}
            >here</Button>
            to see {expand ? 'less' : 'more'}.
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        cursor: 'default !important'

      }}
      >
        <Box sx={{ height: '70px', display: 'flex', justifyContent: 'center', alignItems: 'top', alignSelf: 'start' }}>
          <FactCheckIcon sx={{ height: '40px', width: '40px' }}/>
        </Box>
        <Box sx={{ display: 'block', marginLeft: '18px', heigjustifyContent: 'center', alignItems: 'left' }}>
          {existingRecord[0]?.matchedValues?.map(v => {
            return <Typography sx={{ display: 'flex', flexDirection: 'row' }} variant='body1'>
              {v.type} {v.strength} Match: {v.points} Points
            </Typography>
          })}
          <Typography sx={{ display: 'flex', flexDirection: 'row' }} variant='body1'>
            <>Total Score : <Typography fontWeight={'bold'} sx={{ pl: '10px' }}>{matchValue} Points</Typography></>
          </Typography>
          <Typography sx={{ fontWeight: 'bold', display: 'flex', flexDirection: 'row' }} variant='body1'>
            {result}
          </Typography>

        </Box>
      </AccordionDetails>
    </Accordion>
  </Box>
}

export function DupePanel (props: ChildDuplicate): JSX.Element {
  const loc = TranslateLocation(props)
  const record = TranslateChildDetails(props)
  return <>
    <ChildInfoLayout {...record} hideShowMoreButton />
    {loc != null && (
      <LocationDisplayLayout
        location={loc}
        isHomeless={false}
        isPrimary={false}
      />
    )}
    {props.notes != null && props.notes !== '' && (<>
      Notes: {props.notes}
    </>
    )}
  </>
}
